import { Spinner } from '@common/components';
import React, { FC, useState } from 'react';

import { Routes } from '@common/constants';

import { EntitiesListing } from '@common/features/EntitiesListing';

import { hasAvailability } from '@utils/helpers';
import { Property } from 'shared-types/Property';
import { Category, JourneyParams, Page } from 'shared-types/Common';

import { SpaEntityCategoryView } from '@common/components/SpaEntity/SpaEntityCategoryView';
import {
  useGetSpaSettings,
  useGetSpaTreatments,
  useGetSpaTreatmentsPage,
} from '@common/features/spa/hooks';
import { useGetSpaCategories } from '@common/features/spa/hooks/useGetSpaCategories';
import { Moment } from 'moment';

export type SpaTreatmentsProps = {
  categoryId: number | null;
  property: Property;
  isTreatmentCategoryViewVisible: boolean;
  treatmentsCategories: Category[];
  spaTreatmentPage: Page;
  journeyParams: JourneyParams;
  isLoading: boolean;
  changeJourneyParam: (paramName: string, param: any) => void;
  isFetching: boolean;
  spaCategoryId: number;
  language: string;
};
export const getSpaDateString = (spaDate: string | Moment | undefined) => {
  if (typeof spaDate === 'string') return spaDate;
  return spaDate ? spaDate.format('yyyy-MM-DD') : null;
};
export const SpaTreatments: FC<SpaTreatmentsProps> = ({
  journeyParams,
  property,
  changeJourneyParam,
  language,
  categoryId = null,
}) => {
  const spaDate = journeyParams.spaDate;
  const [categoryFilterId, setCategoryFilterId] = useState(categoryId);

  const { spaTreatments = [], isFetching: isFetchingTreatments } = useGetSpaTreatments(
    property.id,
    language,
    getSpaDateString(spaDate),
    categoryFilterId
  );
  const { data: treatmentsCategories = [], isFetching: isFetchingCategories } = useGetSpaCategories(
    property.id,
    'treatments',
    language
  );
  const { data: spaSettings, isFetching: isFetchingSpaSettings } = useGetSpaSettings(property.id);

  const availableSpaTreatments = spaTreatments
    .sort((a, b) => a.priority - b.priority)
    .filter((item) => hasAvailability(item, journeyParams.fromDate));

  const { data: treatmentPage, isFetching: isFetchingPage } = useGetSpaTreatmentsPage(
    property.id,
    language
  );

  const isFetching =
    isFetchingTreatments || isFetchingCategories || isFetchingPage || isFetchingSpaSettings;

  return (
    <>
      <Spinner isActive={isFetching} showDelayMessage />
      {spaSettings?.isTreatmentCategoryViewVisible && treatmentPage ? (
        <SpaEntityCategoryView
          property={property}
          categories={treatmentsCategories}
          spaEntityPage={treatmentPage}
          journeyParams={journeyParams}
          changeJourneyParam={changeJourneyParam}
          defaultTitle="app.spaTreatments"
          categoryRoute={Routes.SPA_TREATMENT_CATEGORY_DETAIL}
        />
      ) : (
        treatmentPage && (
          <EntitiesListing
            entityRoute={Routes.SPA_TREATMENT_DETAIL}
            entities={availableSpaTreatments.map((t) => ({ ...t, imageGallery: [t.image] }))}
            entitiesAll={spaTreatments}
            entitiesPage={treatmentPage}
            journeyParams={journeyParams}
            entitiesCategories={treatmentsCategories}
            defaultTitle="app.spaTreatments"
            listingRoute={Routes.SPA_TREATMENT_LIST}
            changeJourneyParam={changeJourneyParam}
            setCategoryFilterId={setCategoryFilterId}
            categoryFilterId={categoryFilterId}
          />
        )
      )}
    </>
  );
};
